import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatch, useNavigate } from 'react-router-dom';
import { resources } from '../../core/services/resources';
import { NewCustomViewDialog } from '../../modules/custom-views/NewCustomViewDialog';

interface CustomViewSwitcherProps {
  fullWidth?: boolean;
  /**
   * Active LOB id. The dropdown lists only custom views whose
   * `lineOfBizId` matches it. When `null` the trigger is disabled —
   * there's no LOB context to scope to.
   */
  lineOfBizId: number | null;
}

/**
 * Sidebar selector for a custom view scoped to the active LOB. Selecting
 * an entry navigates to that view's canvas (`/custom-views/:id`). The full
 * tenant catalog is fetched once and filtered to the active LOB
 * client-side so changing LOB doesn't trigger a refetch.
 */
export function CustomViewSwitcher({ fullWidth, lineOfBizId }: CustomViewSwitcherProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  // Drive the "active" state from the /custom-views/:id URL.
  const match = useMatch('/custom-views/:id');
  const activeId = match?.params.id ? Number(match.params.id) : null;

  // Tenant-scoped (via JWT). Shared cache key with any other consumer of
  // the custom-views catalog.
  const viewsQuery = useQuery({
    queryKey: ['custom-views', 'select'],
    queryFn: () => resources.customViews.list({ pageSize: 200, sort: 'name' }),
  });
  const allViews = viewsQuery.data?.data ?? [];

  const views = useMemo(
    () => (lineOfBizId == null ? [] : allViews.filter((v) => v.lineOfBizId === lineOfBizId)),
    [allViews, lineOfBizId],
  );

  const activeName = useMemo(() => {
    if (lineOfBizId == null) return 'Select line of business first';
    if (viewsQuery.isLoading) return 'Loading…';
    if (views.length === 0) return 'No custom views';
    if (activeId != null) {
      const active = views.find((v) => v.id === activeId);
      if (active) return active.name;
    }
    return 'Select custom view';
  }, [activeId, views, viewsQuery.isLoading, lineOfBizId]);

  const open = Boolean(anchor);

  return (
    <>
      <Button
        size="small"
        fullWidth={fullWidth}
        disabled={lineOfBizId == null}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        startIcon={<DashboardCustomizeIcon fontSize="small" />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          minWidth: 200,
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeName}
        </Typography>
      </Button>

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 420 } } }}
      >
        {viewsQuery.isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Loading custom views…
            </Typography>
          </MenuItem>
        )}
        {!viewsQuery.isLoading && views.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No custom views for this line of business
            </Typography>
          </MenuItem>
        )}
        {views.map((v) => {
          const isActive = v.id === activeId;
          return (
            <MenuItem
              key={v.id}
              selected={isActive}
              onClick={() => {
                setAnchor(null);
                navigate(`/custom-views/${v.id}`);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 600 : 400}
                  sx={{ flexGrow: 1 }}
                  noWrap
                >
                  {v.name}
                </Typography>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreateOpen(true);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" fontWeight={600}>
            New custom view
          </Typography>
        </MenuItem>
      </Menu>

      <NewCustomViewDialog
        open={createOpen}
        defaultLineOfBizId={lineOfBizId}
        onClose={() => setCreateOpen(false)}
        onCreated={(view) => {
          navigate(`/custom-views/${view.id}`);
        }}
      />
    </>
  );
}
